import { Input } from "@synthesis/shared-ui";
import { useFormContext, useWatch } from "react-hook-form";

import { isIndividualApplicantType, type ApplicantDataFormValues, type ApplicantType } from "../../create-application-page.lib";
import { ApplicantCheckbox, ApplicantStringList } from "./create-application-applicant-form-controls";
import { CreateApplicationFormField, CreateApplicationFormSection } from "../create-application-form-layout";

type ApplicantContactSectionProps = {
  applicantType: ApplicantType;
  namePrefix?: "payer";
  title?: string;
};

export const ApplicantContactSection = ({ applicantType, namePrefix, title = "Контактная информация" }: ApplicantContactSectionProps) => {
  const methods = useFormContext<ApplicantDataFormValues>();
  const isPayer = namePrefix === "payer";
  const isIndividual = isIndividualApplicantType(applicantType);
  const isIndividualEntrepreneur = applicantType === "SOLE_PROPRIETOR";

  const legalAddressName = isPayer ? "payerLegalAddress" : "legalAddress";
  const actualAddressName = isPayer ? "payerActualAddress" : "actualAddress";
  const postalAddressName = isPayer ? "payerPostalAddress" : "postalAddress";
  const actualAddressMatchesLegalName = isPayer ? "payerActualAddressMatchesLegal" : "actualAddressMatchesLegal";
  const postalAddressMatchesRegistrationName = isPayer ? "payerPostalAddressMatchesRegistration" : "postalAddressMatchesRegistration";
  const phonesName = isPayer ? "payerPhones" : "phones";
  const emailsName = isPayer ? "payerEmails" : "emails";
  const useSeparateContractEmailName = isPayer ? "payerUseSeparateContractEmail" : "useSeparateContractEmail";
  const contractEmailsName = isPayer ? "payerContractEmails" : "contractEmails";
  const useSeparateContractEmail = useWatch({ name: useSeparateContractEmailName });

  const copyActualAddress = (checked: boolean) => {
    if (checked) {
      methods.setValue(actualAddressName, methods.getValues(legalAddressName), { shouldValidate: true });
    }
  };

  const copyPostalAddress = (checked: boolean) => {
    if (checked) {
      methods.setValue(postalAddressName, methods.getValues(legalAddressName), { shouldValidate: true });
    }
  };

  return (
    <CreateApplicationFormSection title={title} required>
      <CreateApplicationFormField label={isIndividual ? "Адрес регистрации" : "Юридический адрес / Адрес регистрации (для ИП)"} required>
        <Input name={legalAddressName} placeholder="индекс, адрес: регион, город, улица, дом (строение)" />
      </CreateApplicationFormField>

      {!isIndividual && (
        <div className="flex flex-col gap-2">
          <CreateApplicationFormField label="Фактический адрес" required>
            <Input name={actualAddressName} placeholder="индекс, адрес: регион, город, улица, дом (строение)" />
          </CreateApplicationFormField>
          <ApplicantCheckbox
            name={actualAddressMatchesLegalName}
            label={isIndividualEntrepreneur ? "Совпадает с адресом регистрации" : "Совпадает с адресом местонахождения"}
            onCheckedChange={copyActualAddress}
          />
        </div>
      )}

      <div className="flex flex-col gap-2">
        <CreateApplicationFormField label="Почтовый адрес" required>
          <Input name={postalAddressName} placeholder="индекс, адрес: регион, город, улица, дом (строение)" />
        </CreateApplicationFormField>
        <ApplicantCheckbox name={postalAddressMatchesRegistrationName} label="Совпадает с адресом регистрации" onCheckedChange={copyPostalAddress} />
      </div>

      <CreateApplicationFormField label={isIndividual ? "Контактный телефон" : "Контактный телефон организации"} required>
        <ApplicantStringList name={phonesName} placeholder="+7 (000) 000 00 00" addLabel="Добавить телефон" removeLabel="Удалить телефон" />
      </CreateApplicationFormField>

      <CreateApplicationFormField label={isIndividual ? "E-mail" : "E-mail организации"} required>
        <ApplicantStringList
          name={emailsName}
          placeholder="example@mail.com"
          addLabel="Добавить e-mail"
          removeLabel="Удалить e-mail"
          footerAction={
            !isIndividual ? (
              <ApplicantCheckbox name={useSeparateContractEmailName} label="Использовать отдельный e-mail для договорных документов" />
            ) : undefined
          }
        />
      </CreateApplicationFormField>

      {!isIndividual && useSeparateContractEmail && (
        <CreateApplicationFormField label="E-mail для направления договора" required>
          <ApplicantStringList name={contractEmailsName} placeholder="example@mail.com" addLabel="Добавить e-mail" removeLabel="Удалить e-mail" />
        </CreateApplicationFormField>
      )}
    </CreateApplicationFormSection>
  );
};
